import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Loader2, Upload, Download, CheckCircle2, XCircle, Trash2, Eye, Image as ImageIcon, FileText, Clock } from 'lucide-react';
import { formatINR, maskAcct } from './payoutUtils';
import { openSecure } from './SettlementDetails';

const Row = ({ p, children, testId }) => (
  <div className="grid grid-cols-[1.2fr_1.4fr_110px_1.6fr] gap-2 items-center px-3 py-2 border-t border-border/40 text-sm" data-testid={testId}>
    <span className="font-mono text-xs">{p.order_id}</span>
    <span className="min-w-0"><span className="block truncate text-xs">{p.beneficiary_name}</span><span className="block text-[10px] text-muted-foreground">{p.bank_name} · {maskAcct(p.account_number_masked)}</span></span>
    <span className="font-mono text-xs text-right">{formatINR(p.amount, 2)}</span>
    {children}
  </div>
);
const Head = ({ last }) => (
  <div className="grid grid-cols-[1.2fr_1.4fr_110px_1.6fr] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/40"><span>Order</span><span>Beneficiary</span><span className="text-right">Amount</span><span>{last}</span></div>
);
const Issue = ({ v, testId }) => v && !v.valid ? <p className="col-span-4 text-[11px] text-red-600 flex items-start gap-1 -mt-1" data-testid={testId}><XCircle className="h-3 w-3 mt-0.5 shrink-0" />{v.errors.join(' · ')}</p> : null;

// ---- UTR entry: one UTR per payout, amount = payout amount ----
export const BulkUtrTab = ({ payouts, utrs, setUtrs, validation }) => {
  const byId = Object.fromEntries((validation?.results || []).map(r => [r.payout_id, r]));
  const filled = payouts.filter(p => (utrs[p.id] || '').trim()).length;
  return (
    <div className="space-y-2" data-testid="bulk-utr-tab">
      <div className="flex items-center justify-between text-xs text-muted-foreground"><span>Enter the bank UTR for each payout. Each UTR must be unique and unused.</span><span data-testid="bulk-utr-filled">{filled}/{payouts.length} entered</span></div>
      <div className="rounded-sm border border-border/50 overflow-hidden max-h-80 overflow-y-auto">
        <Head last="UTR Number" />
        {payouts.map(p => {
          const v = byId[p.id];
          return (
            <React.Fragment key={p.id}>
              <Row p={p} testId={`bulk-utr-row-${p.order_id}`}>
                <div className="flex items-center gap-1.5">
                  <Input value={utrs[p.id] || ''} onChange={e => setUtrs({ ...utrs, [p.id]: e.target.value })} placeholder="e.g. 000547670110" className={`h-8 rounded-sm font-mono text-xs ${v ? (v.valid ? 'border-green-500/60' : 'border-red-500/60') : ''}`} data-testid={`bulk-utr-${p.order_id}`} />
                  {v && (v.valid ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 shrink-0" />)}
                </div>
              </Row>
              {v && !v.valid && <div className="px-3 pb-1.5"><Issue v={v} testId={`bulk-utr-error-${p.order_id}`} /></div>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ---- Screenshot upload: per-payout proof files (stored via the existing proof endpoints) ----
export const BulkScreenshotTab = ({ api, payouts, validation, onChanged }) => {
  const [states, setStates] = useState({});
  const [uploading, setUploading] = useState(null);
  const refs = useRef({});
  const byId = Object.fromEntries((validation?.results || []).map(r => [r.payout_id, r]));

  useEffect(() => {
    let alive = true;
    Promise.all(payouts.map(p => api.get(`/payout-settlements/${p.id}`).then(r => [p.id, r.data]).catch(() => [p.id, null])))
      .then(entries => { if (alive) setStates(Object.fromEntries(entries)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payouts.map(p => p.id).join(',')]);

  const upload = async (p, files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    setUploading(p.id);
    try {
      const r = await api.post(`/payout-settlements/${p.id}/proofs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setStates(s => ({ ...s, [p.id]: r.data }));
      r.data.errors?.length ? toast.error(r.data.errors.join('; ')) : toast.success(`${r.data.added.length} file(s) attached to ${p.order_id}`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); if (refs.current[p.id]) refs.current[p.id].value = ''; }
  };
  const remove = async (p, id) => {
    try { await api.delete(`/payout-settlements/${p.id}/proofs/${id}`); const r = await api.get(`/payout-settlements/${p.id}`); setStates(s => ({ ...s, [p.id]: r.data })); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };
  const withProof = payouts.filter(p => (states[p.id]?.proofs || []).length > 0).length;

  return (
    <div className="space-y-2" data-testid="bulk-screenshot-tab">
      <div className="flex items-center justify-between text-xs text-muted-foreground"><span>PNG, JPG, JPEG or PDF · Max 10MB each · Up to 10 files per payout</span><span data-testid="bulk-proof-count">{withProof}/{payouts.length} have proof</span></div>
      <div className="rounded-sm border border-border/50 overflow-hidden max-h-80 overflow-y-auto">
        <Head last="Settlement Proof" />
        {payouts.map(p => {
          const proofs = states[p.id]?.proofs || [];
          const v = byId[p.id];
          return (
            <React.Fragment key={p.id}>
              <Row p={p} testId={`bulk-proof-row-${p.order_id}`}>
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <input ref={el => { refs.current[p.id] = el; }} type="file" multiple accept=".png,.jpg,.jpeg,.pdf" className="hidden" onChange={e => upload(p, e.target.files)} data-testid={`bulk-proof-input-${p.order_id}`} />
                    <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" disabled={uploading === p.id || proofs.length >= 10} onClick={() => refs.current[p.id]?.click()} data-testid={`bulk-proof-upload-${p.order_id}`}>{uploading === p.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Upload</Button>
                    <span className={`text-[11px] ${proofs.length ? 'text-green-600' : 'text-muted-foreground'}`} data-testid={`bulk-proof-files-${p.order_id}`}>{proofs.length ? `${proofs.length} file(s)` : 'No proof yet'}</span>
                    {v && (v.valid ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 ml-auto" /> : <XCircle className="h-4 w-4 text-red-600 shrink-0 ml-auto" />)}
                  </div>
                  {proofs.map(f => (
                    <div key={f.id} className="flex items-center gap-1.5 text-[11px]">
                      {f.is_image ? <ImageIcon className="h-3 w-3 text-muted-foreground" /> : <FileText className="h-3 w-3 text-muted-foreground" />}
                      <span className="truncate flex-1">{f.original_filename}</span>
                      <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => openSecure(api, f.view_url)} data-testid={`bulk-proof-view-${f.id}`}><Eye className="h-3 w-3" /></button>
                      <button type="button" className="text-red-600" onClick={() => remove(p, f.id)} data-testid={`bulk-proof-remove-${f.id}`}><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              </Row>
              {v && !v.valid && <div className="px-3 pb-1.5"><Issue v={v} testId={`bulk-proof-error-${p.order_id}`} /></div>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ---- Excel upload scoped to the selected payouts ----
export const BulkExcelTab = ({ api, payouts, batch, setBatch, busy, setBusy }) => {
  const ref = useRef(null);
  const template = async () => {
    try {
      const r = await api.get('/payout-settlements/excel/template', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = 'awadhpay_settlement_template.xlsx'; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('payout_ids', payouts.map(p => p.id).join(','));
    setBusy(true);
    try { const r = await api.post('/payout-settlements/excel/validate', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setBatch(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Validation failed'); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const s = batch?.summary;
  const unmatched = batch?.unmatched_payouts || [];

  if (!batch) return (
    <div className="space-y-2" data-testid="bulk-excel-tab">
      <div className="p-6 border border-dashed border-border/60 rounded-sm text-center">
        <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="bulk-excel-input" />
        <Button onClick={() => ref.current?.click()} disabled={busy} className="rounded-sm" data-testid="bulk-excel-upload-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Upload Excel / CSV</Button>
        <p className="text-xs text-muted-foreground mt-2">Rows are matched only against the {payouts.length} selected payout(s) by Order ID, reference or account + amount</p>
      </div>
      <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={template} data-testid="bulk-excel-template-btn"><Download className="h-3.5 w-3.5 mr-1" />Download sample template</Button>
    </div>
  );

  return (
    <div className="space-y-2" data-testid="bulk-excel-tab">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-sm" data-testid="bulk-excel-summary">
        {[['Rows', s.rows], ['Matched', s.matched], ['Amount Match', s.amount_match], ['UTR Valid', s.utr_valid], ['Failed', s.failed, s.failed > 0 && 'text-red-600'], ['Unmatched', unmatched.length, unmatched.length > 0 && 'text-amber-600']].map(([k, v, cls]) => (
          <div key={k} className="p-2 rounded-sm border border-border/50"><p className="text-[10px] uppercase text-muted-foreground">{k}</p><p className={`font-semibold ${cls || ''}`} data-testid={`bulk-excel-${k.toLowerCase().replace(/\s+/g, '-')}`}>{v}</p></div>
        ))}
      </div>
      <div className="flex items-center justify-between p-2.5 rounded-sm bg-green-500/5 border border-green-500/30 text-sm"><span>Completable payouts</span><span className="font-semibold" data-testid="bulk-excel-completable">{s.completable_payouts} of {payouts.length} · {formatINR(s.completable_amount)}</span></div>
      <div className="rounded-sm border border-border/50 overflow-x-auto max-h-56 overflow-y-auto">
        <table className="w-full text-xs min-w-[700px]">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground"><tr><th className="text-left p-2">Row</th><th className="text-left p-2">Order</th><th className="text-right p-2">Amount</th><th className="text-left p-2">UTR</th><th className="text-left p-2">Checks</th></tr></thead>
          <tbody>
            {batch.rows.map(r => (
              <tr key={r.row} className={`border-t border-border/40 ${r.valid ? '' : 'bg-red-500/5'}`} data-testid={`bulk-excel-row-${r.row}`}>
                <td className="p-2 text-muted-foreground">{r.row}</td>
                <td className="p-2 font-mono">{r.order_id || r.order_no || '—'}</td>
                <td className="p-2 text-right font-mono">{r.amount != null ? formatINR(r.amount, 2) : '—'}</td>
                <td className="p-2 font-mono">{r.utr || '—'}</td>
                <td className="p-2">{r.valid ? <span className="text-green-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Matched · Amount verified · UTR unique</span> : r.issues.map((i, k) => <span key={k} className="text-red-600 flex items-center gap-1"><XCircle className="h-3 w-3 shrink-0" />{i}</span>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {unmatched.length > 0 && (
        <div className="p-2.5 rounded-sm border border-amber-500/30 bg-amber-500/5 text-xs space-y-0.5" data-testid="bulk-excel-unmatched">
          <p className="font-medium text-amber-700 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{unmatched.length} selected payout(s) have no valid proof in this file and will stay pending:</p>
          {unmatched.map(u => <p key={u.payout_id} className="font-mono text-muted-foreground">{u.order_id} · {formatINR(u.amount, 2)} · {u.reason}</p>)}
        </div>
      )}
      <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => setBatch(null)} disabled={busy} data-testid="bulk-excel-reset-btn">Upload another file</Button>
    </div>
  );
};
